import { CompletionRecord, Empty, EnvironmentRecord, NormalCompletion, ReferenceRecord } from "./ExecutionContext.js";
import { collectBoundNames } from "./traverse.js";

// https://tc39.es/ecma262/#sec-ispropertyreference
export function IsPropertyReference(V) {
  return V.Base !== "unresolvable" && !(V.Base instanceof EnvironmentRecord);
}

// https://tc39.es/ecma262/#sec-initializereferencedbinding
export function InitializeReferencedBinding(V, W) {
  const base = V.Base;
  return base.InitializeBinding(V.ReferenceName, W);
}

// https://tc39.es/ecma262/#sec-copydataproperties
export function CopyDataProperties(target, source, excludedItems) {
  if (source === undefined || source === null) {
    return target;
  }
  const keys = Object.getOwnPropertyNames(source).concat(Object.getOwnPropertySymbols(source));
  for (const nextKey of keys) {
    if (!excludedItems.has(nextKey)) {
      const desc = Object.getOwnPropertyDescriptor(source, nextKey);
      if (desc !== null && desc !== void 0 && desc.enumerable) {
        target[nextKey] = source[nextKey];
      }
    }
  }
  return target;
}

// https://tc39.es/ecma262/#sec-runtime-semantics-fordeclarationbindinginstantiation
export function ForDeclarationBindingInstantiation(forDeclaration, env) {
  const isConst = forDeclaration.kind === "const";
  for (const name of collectBoundNames(forDeclaration)) {
    if (isConst) {
      env.CreateImmutableBinding(name, true);
    } else {
      env.CreateMutableBinding(name, false);
    }
  }
}

// https://tc39.es/ecma262/#sec-loopcontinues
export function LoopContinues(completion) {
  return completion.Type === "normal" || completion.Type == "continue";
}

// https://tc39.es/ecma262/#sec-updateempty
export function UpdateEmpty(completion, value) {
  if (completion.Value !== Empty) {
    return completion;
  }
  return new CompletionRecord(completion.Type, value);
}

// https://tc39.es/ecma262/#sec-getvalue
export function GetValue(V) {
  if (V instanceof CompletionRecord) {
    // Assert: V.Type is normal.
    V = V.Value;
  }
  if (!(V instanceof ReferenceRecord)) {
    return V;
  }
  if (V.Base === "unresolvable") {
    throw new ReferenceError(`${V.ReferenceName} is not defined`);
  }
  if (V.Base instanceof EnvironmentRecord) {
    const base = V.Base;
    return base.GetBindingValue(V.ReferenceName, V.Strict);
  }
  return V.Base[V.ReferenceName];
}

// https://tc39.es/ecma262/#sec-topropertykey
export function ToPropertyKey(arg) {
  if (typeof arg === "symbol") {
    return arg;
  }
  return String(arg);
}

// https://tc39.es/ecma262/#sec-getv
export function GetV(V, P) {
  return V[P];
}

// https://tc39.es/ecma262/#sec-putvalue
export function PutValue(V, W) {
  // Assert: V is a ReferenceRecord.
  if (V.Base === "unresolvable") {
    throw new ReferenceError(`${V.ReferenceName} is not defined`);
  }
  if (V.Base instanceof EnvironmentRecord) {
    return V.Base.SetMutableBinding(V.ReferenceName, W, V.Strict);
  }
  V.Base[V.ReferenceName] = W;
  return NormalCompletion(undefined);
}

// https://tc39.es/ecma262/#sec-createlistiteratorRecord
export function CreateListIteratorRecord(args) {
  if (!isIterable(args)) {
    throw new TypeError(`${typeof args} is not iterable`);
  }
  return args[Symbol.iterator]();
}

// https://tc39.es/ecma262/#sec-requireobjectcoercible
export function RequireObjectCoercible(arg) {
  if (arg === null || arg === undefined) {
    throw new TypeError("Cannot destructure properties of undefined or null");
  }
}

// https://tc39.es/ecma262/#sec-getidentifierreference
export function GetIdentifierReference(env, name, strict) {
  if (!env) {
    return new ReferenceRecord("unresolvable", name, strict);
  }
  if (env.HasBinding(name)) {
    return new ReferenceRecord(env, name, strict);
  }
  return GetIdentifierReference(env.OuterEnv, name, strict);
}

// https://tc39.es/ecma262/#sec-applystringornumericbinaryoperator
export function ApplyStringOrNumericBinaryOperator(leftValue, operator, rightValue) {
  switch (operator) {
    case "+":
      return leftValue + rightValue;
    case "-":
      return leftValue - rightValue;
    case "/":
      return leftValue / rightValue;
    case "%":
      return leftValue % rightValue;
    case "*":
      return leftValue * rightValue;
    case "**":
      return leftValue ** rightValue;
    case "==":
      return leftValue == rightValue;
    case "===":
      return leftValue === rightValue;
    case "!=":
      return leftValue != rightValue;
    case "!==":
      return leftValue !== rightValue;
    case ">":
      return leftValue > rightValue;
    case "<":
      return leftValue < rightValue;
    case ">=":
      return leftValue >= rightValue;
    case "<=":
      return leftValue <= rightValue;
    case "in":
      return leftValue in rightValue;
  }
  throw new SyntaxError(`Unsupported binary operator \`${operator}\``);
}

// https://tc39.es/ecma262/#sec-assignment-operators
export function ApplyStringOrNumericAssignment(leftValue, operator, rightValue) {
  switch (operator) {
    case "+=":
    case "-=":
    case "*=":
    case "/=":
    case "%=":
    case "**=":
      return ApplyStringOrNumericBinaryOperator(leftValue, operator.substr(0, operator.length - 1), rightValue);
  }
  throw new SyntaxError(`Unsupported assignment operator \`${operator}\``);
}

// https://tc39.es/ecma262/#sec-unary-operators
export function ApplyUnaryOperator(target, operator) {
  switch (operator) {
    case "!":
      return !target;
    case "+":
      return +target;
    case "-":
      return -target;
    case "void":
      return undefined;
  }
  throw new SyntaxError(`Unsupported unary operator \`${operator}\``);
}
export function isIterable(cooked) {
  if (Array.isArray(cooked)) {
    return true;
  }
  if (cooked === null || cooked === undefined) {
    return false;
  }
  return typeof cooked[Symbol.iterator] === "function";
}
//# sourceMappingURL=context-free.js.map